import { useEffect, useState } from 'react';
import { getOrganizationContracts } from '@/services/contracts/contractService';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { FileText } from 'lucide-react';

interface ContractsTabProps {
  organizationId: string;
}

export function ContractsTab({ organizationId }: ContractsTabProps) {
  const [contracts, setContracts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadContracts = async () => {
      try {
        const data = await getOrganizationContracts(organizationId);
        setContracts(data);
      } catch (error) {
        // Error handling omitted intentionally
      } finally {
        setLoading(false);
      }
    };

    loadContracts();
  }, [organizationId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-500';
      case 'sent':
        return 'bg-blue-500';
      case 'signed':
        return 'bg-green-500';
      case 'expired':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (loading) {
    return <div>Loading contracts...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Contracts</h2>
        <Button onClick={() => navigate(`/contracts/generate/${organizationId}`)}>
          <FileText className="w-4 h-4 mr-2" />
          Generate New Contract
        </Button>
      </div>

      {contracts.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No contracts generated yet</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contracts.map((contract) => (
              <TableRow key={contract.id}>
                <TableCell className="font-medium">{contract.type.toUpperCase()}</TableCell>
                <TableCell>{format(new Date(contract.created_at), 'MMM d, yyyy')}</TableCell>
                <TableCell>
                  <Badge className={getStatusColor(contract.status)}>{contract.status}</Badge>
                </TableCell>
                <TableCell>
                  <Button variant="outline" size="sm">
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
